import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../hooks/useToast'
import {
  ArrowLeft, Shield, Check, X, Loader, Activity,
  Users as UsersIcon, Search, Save, ChevronDown, ChevronUp,
  Monitor, Grid3X3, Phone, Mail, Building2, UserPlus
} from 'lucide-react'
import { UserActivityDashboard } from './UserActivityDashboard'
import { CHWatchAdmin } from './CHWatchAdmin'

interface User {
  id: string
  email: string
  full_name: string | null
  company: string | null
  phone_number: string | null
  is_active: boolean
  created_at: string | null
  last_login: string | null
}

interface Application {
  id: string
  name: string
  description: string
  app_type: 'native' | 'external'
  is_active: boolean
}

interface UserAccess {
  user_id: string
  application_id: string
}

interface UserEditState {
  full_name: string
  company: string
  phone_number: string
}

type ActiveView = 'activity' | 'users' | 'applications' | 'matrix' | 'chwatch'

// Shared styles
const FONT = 'Lexend Deca, sans-serif'

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280',
  marginBottom: '4px', fontFamily: FONT
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: '8px',
  border: '1px solid #d1d5db', fontSize: '14px',
  fontFamily: FONT, background: 'white', outline: 'none',
  boxSizing: 'border-box'
}

const primaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center',
  padding: '8px 16px', background: '#2563eb', color: 'white',
  fontWeight: '600', borderRadius: '8px', border: 'none',
  cursor: 'pointer', fontSize: '13px', fontFamily: FONT
}

const secondaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center',
  padding: '8px 16px', background: 'white', color: '#374151',
  fontWeight: '500', borderRadius: '8px', border: '1px solid #d1d5db',
  cursor: 'pointer', fontSize: '13px', fontFamily: FONT
}

const dangerBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center',
  padding: '8px 16px', background: '#fee2e2', color: '#991b1b',
  fontWeight: '500', borderRadius: '8px', border: '1px solid #fecaca',
  cursor: 'pointer', fontSize: '13px', fontFamily: FONT
}

const typeBadge = (appType: string): React.CSSProperties => ({
  padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '500',
  fontFamily: FONT,
  background: appType === 'native' ? '#e0e7ff' : '#fef3c7',
  color: appType === 'native' ? '#3730a3' : '#92400e'
})

export const AdminPanel: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [users, setUsers] = useState<User[]>([])
  const [applications, setApplications] = useState<Application[]>([])
  const [userAccess, setUserAccess] = useState<UserAccess[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeView, setActiveView] = useState<ActiveView>('activity')

  // Users tab state
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null)
  const [userEditState, setUserEditState] = useState<UserEditState | null>(null)
  const [savingProfile, setSavingProfile] = useState(false)

  // Create user state
  const [showCreateUser, setShowCreateUser] = useState(false)
  const [createEmail, setCreateEmail] = useState('')
  const [createFullName, setCreateFullName] = useState('')
  const [createCompany, setCreateCompany] = useState('')
  const [creatingUser, setCreatingUser] = useState(false)

  // Applications tab state
  const [appSearchQuery, setAppSearchQuery] = useState('')
  const [expandedAppId, setExpandedAppId] = useState<string | null>(null)

  const ADMIN_USER_ID = '89927d9d-5bb1-41af-8755-01ffd69d960f'

  useEffect(() => {
    if (!user) return
    if (user.id !== ADMIN_USER_ID) {
      navigate('/')
      return
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate])

  const showMessage = (type: 'success' | 'error', text: string) => {
    showToast(type, text)
  }

  const fetchData = async () => {
    try {
      const { data: usersData, error: usersError } = await supabase
        .rpc('get_all_users_with_profiles')

      let mergedUsers: User[] = []

      if (usersError || !usersData) {
        const { data: profilesData } = await supabase
          .from('user_profiles')
          .select('*')
          .order('full_name')

        mergedUsers = profilesData?.map(profile => ({
          id: profile.id,
          email: 'Email not available',
          full_name: profile.full_name,
          company: profile.company,
          phone_number: profile.phone_number,
          is_active: profile.is_active ?? true,
          created_at: profile.created_at,
          last_login: profile.last_login
        })) || []
      } else {
        mergedUsers = usersData
      }

      const { data: appsData } = await supabase
        .from('applications')
        .select('id, name, description, app_type, is_active')
        .order('name')

      const { data: accessData } = await supabase
        .from('user_app_access')
        .select('user_id, application_id')

      setUsers(mergedUsers)
      setApplications(appsData || [])
      setUserAccess(accessData || [])
      setLoading(false)
    } catch (error) {
      console.error('Error fetching data:', error)
      showMessage('error', 'Failed to load data')
      setLoading(false)
    }
  }

  // Helpers
  const hasAccess = (userId: string, appId: string) =>
    userAccess.some(a => a.user_id === userId && a.application_id === appId)

  const getUserAppCount = (userId: string) =>
    userAccess.filter(a => a.user_id === userId).length

  const getAppUserCount = (appId: string) =>
    userAccess.filter(a => a.application_id === appId).length

  const filteredUsers = users.filter(u => {
    if (!userSearchQuery) return true
    const q = userSearchQuery.toLowerCase()
    return (
      (u.full_name?.toLowerCase().includes(q)) ||
      u.email.toLowerCase().includes(q) ||
      (u.company?.toLowerCase().includes(q))
    )
  })

  const filteredApps = applications.filter(a => {
    if (!appSearchQuery) return true
    const q = appSearchQuery.toLowerCase()
    return (
      a.name.toLowerCase().includes(q) ||
      a.description.toLowerCase().includes(q) ||
      a.app_type.toLowerCase().includes(q)
    )
  })

  // Handlers
  const toggleAccess = async (userId: string, appId: string) => {
    setSaving(true)
    try {
      if (hasAccess(userId, appId)) {
        const { error } = await supabase
          .from('user_app_access')
          .delete()
          .eq('user_id', userId)
          .eq('application_id', appId)
        if (error) throw error
        setUserAccess(prev => prev.filter(
          a => !(a.user_id === userId && a.application_id === appId)
        ))
        showMessage('success', 'Access revoked')
      } else {
        const { error } = await supabase
          .from('user_app_access')
          .insert({ user_id: userId, application_id: appId, granted_by: user?.id })
        if (error) throw error
        setUserAccess(prev => [...prev, { user_id: userId, application_id: appId }])
        showMessage('success', 'Access granted')
      }
    } catch (error) {
      console.error('Error toggling access:', error)
      showMessage('error', 'Failed to update access')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveProfile = async (userId: string) => {
    if (!userEditState) return
    setSavingProfile(true)
    try {
      const { error } = await supabase.rpc('admin_update_user_profile', {
        target_user_id: userId,
        new_full_name: userEditState.full_name,
        new_company: userEditState.company,
        new_phone_number: userEditState.phone_number  // '' clears, value sets, omitted keeps
      })
      if (error) throw error
      setUsers(prev => prev.map(u =>
        u.id === userId
          ? {
              ...u,
              full_name: userEditState.full_name,
              company: userEditState.company,
              phone_number: userEditState.phone_number || null
            }
          : u
      ))
      showMessage('success', 'Profile updated')
    } catch (error) {
      console.error('Error updating profile:', error)
      showMessage('error', 'Failed to update profile')
    } finally {
      setSavingProfile(false)
    }
  }

  const handleToggleUserActive = async (userId: string, currentActive: boolean) => {
    setSaving(true)
    try {
      const { error } = await supabase.rpc('admin_update_user_profile', {
        target_user_id: userId,
        new_is_active: !currentActive
      })
      if (error) throw error
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, is_active: !currentActive } : u
      ))
      showMessage('success', `User ${!currentActive ? 'activated' : 'deactivated'}`)
    } catch (error) {
      console.error('Error toggling user active:', error)
      showMessage('error', 'Failed to update user status')
    } finally {
      setSaving(false)
    }
  }

  const grantAllUsersToApp = async (appId: string) => {
    setSaving(true)
    try {
      const usersWithout = users.filter(u => !hasAccess(u.id, appId))
      if (usersWithout.length === 0) {
        showMessage('success', 'All users already have access')
        setSaving(false)
        return
      }
      const inserts = usersWithout.map(u => ({
        user_id: u.id, application_id: appId, granted_by: user?.id
      }))
      const { error } = await supabase.from('user_app_access').insert(inserts)
      if (error) throw error
      const newAccess = usersWithout.map(u => ({ user_id: u.id, application_id: appId }))
      setUserAccess(prev => [...prev, ...newAccess])
      showMessage('success', `Access granted to ${usersWithout.length} users`)
    } catch (error) {
      console.error('Error granting access:', error)
      showMessage('error', 'Failed to grant access')
    } finally {
      setSaving(false)
    }
  }

  const revokeAllUsersFromApp = async (appId: string) => {
    setSaving(true)
    try {
      const { error } = await supabase
        .from('user_app_access')
        .delete()
        .eq('application_id', appId)
      if (error) throw error
      setUserAccess(prev => prev.filter(a => a.application_id !== appId))
      showMessage('success', 'All access revoked for this application')
    } catch (error) {
      console.error('Error revoking access:', error)
      showMessage('error', 'Failed to revoke access')
    } finally {
      setSaving(false)
    }
  }

  const handleCreateUser = async () => {
    if (!createEmail.trim()) {
      showMessage('error', 'Email is required')
      return
    }
    setCreatingUser(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          email: createEmail.trim(),
          full_name: createFullName.trim() || null,
          company: createCompany.trim() || null
        })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to create user')

      // Add the new user to local state
      setUsers(prev => [...prev, {
        id: data.user.id,
        email: data.user.email,
        full_name: createFullName.trim() || null,
        company: createCompany.trim() || null,
        phone_number: null,
        is_active: true,
        created_at: new Date().toISOString(),
        last_login: null
      }])

      setCreateEmail('')
      setCreateFullName('')
      setCreateCompany('')
      setShowCreateUser(false)
      showMessage('success', 'User created successfully')
    } catch (error: any) {
      console.error('Error creating user:', error)
      showMessage('error', error.message || 'Failed to create user')
    } finally {
      setCreatingUser(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader style={{
            height: '48px', width: '48px', color: '#2563eb',
            animation: 'spin 1s linear infinite', margin: '0 auto 16px'
          }} />
          <p style={{ color: '#6b7280', fontFamily: FONT }}>Loading admin panel...</p>
        </div>
      </div>
    )
  }

  const activeUsers = users.filter(u => u.is_active).length

  const tabs: { key: ActiveView; label: string; icon: React.ElementType }[] = [
    { key: 'activity', label: 'Activity', icon: Activity },
    { key: 'users', label: 'Users', icon: UsersIcon },
    { key: 'applications', label: 'Applications', icon: Monitor },
    { key: 'matrix', label: 'Access Matrix', icon: Grid3X3 },
    { key: 'chwatch', label: 'CH Watch', icon: Building2 },
  ]

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)',
      padding: '48px 16px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '8px 16px', background: 'white', color: '#2563eb',
              fontWeight: '600', borderRadius: '8px', border: '1px solid #e5e7eb',
              cursor: 'pointer', fontSize: '14px', fontFamily: FONT, marginBottom: '24px'
            }}
          >
            <ArrowLeft style={{ height: '16px', width: '16px', marginRight: '8px' }} />
            Back to Dashboard
          </button>

          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <Shield style={{ height: '32px', width: '32px', color: '#dc2626', marginRight: '12px' }} />
            <h1 style={{ fontSize: '36px', fontWeight: 'bold', color: '#111827', fontFamily: FONT }}>
              Admin Panel
            </h1>
          </div>
          <p style={{ fontSize: '18px', color: '#6b7280', fontFamily: FONT }}>
            Manage users, applications, and access permissions
          </p>
        </div>

        {/* Summary Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px', marginBottom: '24px'
        }}>
          {[
            { label: 'Total Users', value: users.length, color: '#2563eb' },
            { label: 'Active Users', value: activeUsers, color: '#10b981' },
            { label: 'Applications', value: applications.length, color: '#8b5cf6' },
            { label: 'Access Grants', value: userAccess.length, color: '#f59e0b' },
          ].map(stat => (
            <div key={stat.label} style={{
              background: 'white', padding: '20px', borderRadius: '12px',
              textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: stat.color, fontFamily: FONT }}>
                {stat.value}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280', fontFamily: FONT }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Tab Bar */}
        <div style={{
          display: 'flex', gap: '8px', marginBottom: '24px',
          background: 'white', borderRadius: '12px', padding: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          {tabs.map(tab => {
            const Icon = tab.icon
            const isActive = activeView === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => setActiveView(tab.key)}
                style={{
                  flex: 1, padding: '12px 24px',
                  background: isActive ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                  color: isActive ? 'white' : '#6b7280',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontSize: '14px', fontWeight: '600', fontFamily: FONT,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '8px', transition: 'all 0.2s ease'
                }}
              >
                <Icon style={{ height: '16px', width: '16px' }} />
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Activity Tab */}
        {activeView === 'activity' && <UserActivityDashboard />}
        {activeView === 'chwatch' && <CHWatchAdmin />}

        {/* Users Tab */}
        {activeView === 'users' && (
          <div>
            {/* Create User */}
            <div style={{ marginBottom: '16px' }}>
              <button
                onClick={() => setShowCreateUser(!showCreateUser)}
                style={{
                  ...primaryBtnStyle,
                  background: showCreateUser ? '#6b7280' : '#2563eb'
                }}
              >
                <UserPlus style={{ height: '16px', width: '16px', marginRight: '8px' }} />
                {showCreateUser ? 'Cancel' : 'Add User'}
              </button>

              {showCreateUser && (
                <div style={{
                  background: 'white', borderRadius: '12px', padding: '20px',
                  marginTop: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '2px solid #2563eb'
                }}>
                  <h3 style={{
                    fontSize: '16px', fontWeight: '600', color: '#111827',
                    fontFamily: FONT, marginBottom: '16px'
                  }}>
                    Create New User
                  </h3>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '16px', marginBottom: '16px'
                  }}>
                    <div>
                      <label style={labelStyle}>Email *</label>
                      <input
                        type="email"
                        placeholder="user@example.com"
                        value={createEmail}
                        onChange={e => setCreateEmail(e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Full Name</label>
                      <input
                        type="text"
                        placeholder="John Smith"
                        value={createFullName}
                        onChange={e => setCreateFullName(e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Company</label>
                      <input
                        type="text"
                        placeholder="Acme Corp"
                        value={createCompany}
                        onChange={e => setCreateCompany(e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      onClick={handleCreateUser}
                      disabled={creatingUser || !createEmail.trim()}
                      style={{
                        ...primaryBtnStyle,
                        opacity: (creatingUser || !createEmail.trim()) ? 0.6 : 1,
                        cursor: (creatingUser || !createEmail.trim()) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {creatingUser ? (
                        <>
                          <Loader style={{
                            height: '14px', width: '14px', marginRight: '6px',
                            animation: 'spin 1s linear infinite'
                          }} />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Check style={{ height: '14px', width: '14px', marginRight: '6px' }} />
                          Create User
                        </>
                      )}
                    </button>
                    <span style={{ fontSize: '12px', color: '#6b7280', fontFamily: FONT }}>
                      User will be created with no application access
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Search */}
            <div style={{
              background: 'white', borderRadius: '12px', padding: '16px',
              marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex', alignItems: 'center', gap: '12px'
            }}>
              <Search style={{ height: '20px', width: '20px', color: '#9ca3af', flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search users by name, email, or company..."
                value={userSearchQuery}
                onChange={e => setUserSearchQuery(e.target.value)}
                style={{
                  flex: 1, border: 'none', outline: 'none', fontSize: '14px',
                  fontFamily: FONT, color: '#111827', background: 'transparent'
                }}
              />
              {userSearchQuery && (
                <button
                  onClick={() => setUserSearchQuery('')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                >
                  <X style={{ height: '16px', width: '16px', color: '#9ca3af' }} />
                </button>
              )}
            </div>

            <p style={{ fontSize: '13px', color: '#6b7280', fontFamily: FONT, marginBottom: '12px' }}>
              {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
              {userSearchQuery && ` matching "${userSearchQuery}"`}
            </p>

            {/* User List */}
            {filteredUsers.map(u => {
              const isExpanded = expandedUserId === u.id
              const appCount = getUserAppCount(u.id)

              return (
                <div key={u.id} style={{
                  background: 'white', borderRadius: '12px', marginBottom: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden',
                  border: isExpanded ? '2px solid #2563eb' : '1px solid #e5e7eb'
                }}>
                  {/* Row header */}
                  <div
                    onClick={() => {
                      if (isExpanded) {
                        setExpandedUserId(null)
                        setUserEditState(null)
                      } else {
                        setExpandedUserId(u.id)
                        setUserEditState({ full_name: u.full_name || '', company: u.company || '', phone_number: u.phone_number || '' })
                      }
                    }}
                    style={{
                      padding: '16px 20px', display: 'flex', alignItems: 'center',
                      cursor: 'pointer', gap: '16px'
                    }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '50%',
                      background: u.is_active ? '#e0e7ff' : '#f3f4f6',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <UsersIcon style={{
                        height: '20px', width: '20px',
                        color: u.is_active ? '#3730a3' : '#9ca3af'
                      }} />
                    </div>

                    {/* Name + email */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827', fontFamily: FONT }}>
                        {u.full_name || 'No name'}
                      </div>
                      <div style={{
                        fontSize: '13px', color: '#6b7280', fontFamily: FONT,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {u.email}
                      </div>
                    </div>

                    {/* Company */}
                    <div style={{
                      fontSize: '13px', color: '#6b7280', fontFamily: FONT,
                      minWidth: '80px', display: 'none'
                    }}
                    className="admin-hide-mobile"
                    >
                      {u.company || '-'}
                    </div>

                    {/* Active badge */}
                    <span style={{
                      padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
                      fontFamily: FONT, flexShrink: 0,
                      background: u.is_active ? '#dcfce7' : '#fee2e2',
                      color: u.is_active ? '#166534' : '#991b1b'
                    }}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>

                    {/* App count badge */}
                    <span style={{
                      padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
                      fontFamily: FONT, background: '#e0e7ff', color: '#3730a3', flexShrink: 0
                    }}>
                      {appCount} app{appCount !== 1 ? 's' : ''}
                    </span>

                    {/* Chevron */}
                    {isExpanded
                      ? <ChevronUp style={{ height: '20px', width: '20px', color: '#6b7280', flexShrink: 0 }} />
                      : <ChevronDown style={{ height: '20px', width: '20px', color: '#6b7280', flexShrink: 0 }} />
                    }
                  </div>

                  {/* Expanded panel */}
                  {isExpanded && userEditState && (
                    <div style={{ padding: '0 20px 20px', borderTop: '1px solid #f3f4f6' }}>
                      {/* Profile edit */}
                      <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '16px', marginTop: '16px', marginBottom: '16px'
                      }}>
                        <div>
                          <label style={labelStyle}>Full Name</label>
                          <input
                            value={userEditState.full_name}
                            onChange={e => setUserEditState({ ...userEditState, full_name: e.target.value })}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Company</label>
                          <input
                            value={userEditState.company}
                            onChange={e => setUserEditState({ ...userEditState, company: e.target.value })}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Phone (SMS OTP)</label>
                          <input
                            type="tel"
                            value={userEditState.phone_number}
                            onChange={e => setUserEditState({ ...userEditState, phone_number: e.target.value })}
                            placeholder="+44 7911 123456"
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      {/* Read-only info */}
                      <div style={{
                        display: 'flex', gap: '24px', marginBottom: '16px',
                        fontSize: '13px', color: '#6b7280', fontFamily: FONT, flexWrap: 'wrap'
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Phone style={{ height: '14px', width: '14px' }} />
                          {u.phone_number || 'No phone'}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Mail style={{ height: '14px', width: '14px' }} />
                          {u.email}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Building2 style={{ height: '14px', width: '14px' }} />
                          {u.company || 'No company'}
                        </span>
                      </div>

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center' }}>
                        <button
                          onClick={() => handleSaveProfile(u.id)}
                          disabled={savingProfile}
                          style={{
                            ...primaryBtnStyle,
                            opacity: savingProfile ? 0.6 : 1,
                            cursor: savingProfile ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <Save style={{ height: '14px', width: '14px', marginRight: '6px' }} />
                          {savingProfile ? 'Saving...' : 'Save Profile'}
                        </button>
                        <button
                          onClick={() => handleToggleUserActive(u.id, u.is_active)}
                          disabled={saving}
                          style={{
                            ...(u.is_active ? dangerBtnStyle : secondaryBtnStyle),
                            opacity: saving ? 0.6 : 1,
                            cursor: saving ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {u.is_active ? 'Deactivate User' : 'Activate User'}
                        </button>
                      </div>

                      {/* App access */}
                      <h4 style={{
                        fontSize: '14px', fontWeight: '600', color: '#111827',
                        marginBottom: '12px', fontFamily: FONT
                      }}>
                        Application Access
                      </h4>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {applications.map(app => {
                          const granted = hasAccess(u.id, app.id)
                          return (
                            <div key={app.id} style={{
                              padding: '10px 12px', borderRadius: '8px',
                              border: '1px solid #e5e7eb',
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              background: granted ? '#f0fdf4' : '#fafafa'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827', fontFamily: FONT }}>
                                  {app.name}
                                </span>
                                <span style={typeBadge(app.app_type)}>{app.app_type}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                  type="checkbox"
                                  checked={granted}
                                  onChange={() => toggleAccess(u.id, app.id)}
                                  disabled={saving}
                                  style={{
                                    width: '18px', height: '18px',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                    accentColor: '#10b981'
                                  }}
                                />
                                <span style={{
                                  fontSize: '12px', fontFamily: FONT,
                                  color: granted ? '#10b981' : '#9ca3af'
                                }}>
                                  {granted ? 'Granted' : 'None'}
                                </span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Applications Tab */}
        {activeView === 'applications' && (
          <div>
            {/* Search */}
            <div style={{
              background: 'white', borderRadius: '12px', padding: '16px',
              marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex', alignItems: 'center', gap: '12px'
            }}>
              <Search style={{ height: '20px', width: '20px', color: '#9ca3af', flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search applications by name, description, or type..."
                value={appSearchQuery}
                onChange={e => setAppSearchQuery(e.target.value)}
                style={{
                  flex: 1, border: 'none', outline: 'none', fontSize: '14px',
                  fontFamily: FONT, color: '#111827', background: 'transparent'
                }}
              />
              {appSearchQuery && (
                <button
                  onClick={() => setAppSearchQuery('')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                >
                  <X style={{ height: '16px', width: '16px', color: '#9ca3af' }} />
                </button>
              )}
            </div>

            <p style={{ fontSize: '13px', color: '#6b7280', fontFamily: FONT, marginBottom: '12px' }}>
              {filteredApps.length} application{filteredApps.length !== 1 ? 's' : ''}
              {appSearchQuery && ` matching "${appSearchQuery}"`}
            </p>

            {/* App List */}
            {filteredApps.map(app => {
              const isExpanded = expandedAppId === app.id
              const userCount = getAppUserCount(app.id)

              return (
                <div key={app.id} style={{
                  background: 'white', borderRadius: '12px', marginBottom: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden',
                  border: isExpanded ? '2px solid #2563eb' : '1px solid #e5e7eb'
                }}>
                  {/* Row header */}
                  <div
                    onClick={() => setExpandedAppId(isExpanded ? null : app.id)}
                    style={{
                      padding: '16px 20px', display: 'flex', alignItems: 'center',
                      cursor: 'pointer', gap: '16px'
                    }}
                  >
                    {/* Icon */}
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '50%',
                      background: app.is_active ? '#f0fdf4' : '#f3f4f6',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Monitor style={{
                        height: '20px', width: '20px',
                        color: app.is_active ? '#166534' : '#9ca3af'
                      }} />
                    </div>

                    {/* Name + description */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827', fontFamily: FONT }}>
                          {app.name}
                        </span>
                        <span style={typeBadge(app.app_type)}>{app.app_type}</span>
                      </div>
                      <div style={{
                        fontSize: '13px', color: '#6b7280', fontFamily: FONT,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {app.description}
                      </div>
                    </div>

                    {/* Status */}
                    <span style={{
                      padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
                      fontFamily: FONT, flexShrink: 0,
                      background: app.is_active ? '#dcfce7' : '#fee2e2',
                      color: app.is_active ? '#166534' : '#991b1b'
                    }}>
                      {app.is_active ? 'Active' : 'Inactive'}
                    </span>

                    {/* User count badge */}
                    <span style={{
                      padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
                      fontFamily: FONT, background: '#e0e7ff', color: '#3730a3', flexShrink: 0
                    }}>
                      {userCount} user{userCount !== 1 ? 's' : ''}
                    </span>

                    {/* Chevron */}
                    {isExpanded
                      ? <ChevronUp style={{ height: '20px', width: '20px', color: '#6b7280', flexShrink: 0 }} />
                      : <ChevronDown style={{ height: '20px', width: '20px', color: '#6b7280', flexShrink: 0 }} />
                    }
                  </div>

                  {/* Expanded panel */}
                  {isExpanded && (
                    <div style={{ padding: '0 20px 20px', borderTop: '1px solid #f3f4f6' }}>
                      {/* Batch actions */}
                      <div style={{ display: 'flex', gap: '12px', margin: '16px 0' }}>
                        <button
                          onClick={() => grantAllUsersToApp(app.id)}
                          disabled={saving}
                          style={{
                            ...primaryBtnStyle,
                            opacity: saving ? 0.6 : 1,
                            cursor: saving ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Grant All Users
                        </button>
                        <button
                          onClick={() => revokeAllUsersFromApp(app.id)}
                          disabled={saving}
                          style={{
                            ...dangerBtnStyle,
                            opacity: saving ? 0.6 : 1,
                            cursor: saving ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Revoke All Users
                        </button>
                      </div>

                      {/* User checkboxes */}
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {users.map(u => {
                          const granted = hasAccess(u.id, app.id)
                          return (
                            <div key={u.id} style={{
                              padding: '10px 12px', borderRadius: '8px',
                              border: '1px solid #e5e7eb',
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              background: granted ? '#f0fdf4' : '#fafafa'
                            }}>
                              <div>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827', fontFamily: FONT }}>
                                  {u.full_name || 'No name'}
                                </span>
                                <span style={{
                                  fontSize: '12px', color: '#6b7280', marginLeft: '8px', fontFamily: FONT
                                }}>
                                  {u.email}
                                </span>
                                {!u.is_active && (
                                  <span style={{
                                    fontSize: '11px', color: '#991b1b', marginLeft: '8px',
                                    fontFamily: FONT, background: '#fee2e2', padding: '1px 6px',
                                    borderRadius: '4px'
                                  }}>
                                    inactive
                                  </span>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                checked={granted}
                                onChange={() => toggleAccess(u.id, app.id)}
                                disabled={saving}
                                style={{
                                  width: '18px', height: '18px',
                                  cursor: saving ? 'not-allowed' : 'pointer',
                                  accentColor: '#10b981'
                                }}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Access Matrix Tab */}
        {activeView === 'matrix' && (
          <div style={{
            background: 'white', borderRadius: '16px', padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto'
          }}>
            <h2 style={{
              fontSize: '20px', fontWeight: 'bold', color: '#111827',
              marginBottom: '20px', fontFamily: FONT
            }}>
              Access Matrix
            </h2>

            <table style={{
              width: '100%', borderCollapse: 'collapse',
              minWidth: `${220 + applications.length * 100}px`
            }}>
              <thead>
                <tr>
                  <th style={{
                    padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600',
                    color: '#6b7280', fontFamily: FONT, borderBottom: '2px solid #e5e7eb',
                    position: 'sticky', left: 0, background: 'white', zIndex: 2,
                    minWidth: '200px'
                  }}>
                    User
                  </th>
                  {applications.map(app => (
                    <th key={app.id} style={{
                      padding: '8px 4px', textAlign: 'center',
                      borderBottom: '2px solid #e5e7eb', verticalAlign: 'bottom',
                      minWidth: '80px'
                    }} title={app.name}>
                      <div style={{
                        writingMode: 'vertical-lr', transform: 'rotate(180deg)',
                        fontSize: '12px', fontWeight: '600', color: '#6b7280',
                        fontFamily: FONT, maxHeight: '120px', overflow: 'hidden',
                        textOverflow: 'ellipsis', margin: '0 auto'
                      }}>
                        {app.name}
                      </div>
                    </th>
                  ))}
                  <th style={{
                    padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600',
                    color: '#374151', fontFamily: FONT, borderBottom: '2px solid #e5e7eb',
                    background: '#f9fafb'
                  }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, idx) => (
                  <tr key={u.id} style={{
                    borderBottom: '1px solid #f3f4f6',
                    background: idx % 2 === 0 ? 'white' : '#fafafa'
                  }}>
                    <td style={{
                      padding: '10px 12px', position: 'sticky', left: 0,
                      background: idx % 2 === 0 ? 'white' : '#fafafa',
                      zIndex: 1, fontFamily: FONT
                    }}>
                      <div style={{ fontSize: '13px', fontWeight: '500', color: '#111827' }}>
                        {u.full_name || 'No name'}
                        {!u.is_active && (
                          <span style={{
                            fontSize: '10px', color: '#991b1b', marginLeft: '6px',
                            background: '#fee2e2', padding: '1px 4px', borderRadius: '3px'
                          }}>
                            inactive
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>{u.email}</div>
                    </td>
                    {applications.map(app => {
                      const granted = hasAccess(u.id, app.id)
                      return (
                        <td key={app.id} style={{ padding: '8px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={granted}
                            onChange={() => toggleAccess(u.id, app.id)}
                            disabled={saving}
                            style={{
                              width: '18px', height: '18px',
                              cursor: saving ? 'not-allowed' : 'pointer',
                              accentColor: '#10b981'
                            }}
                          />
                        </td>
                      )
                    })}
                    <td style={{
                      padding: '8px', textAlign: 'center', fontWeight: '600', fontSize: '13px',
                      color: '#374151', fontFamily: FONT, background: '#f9fafb'
                    }}>
                      {getUserAppCount(u.id)}
                    </td>
                  </tr>
                ))}
                {/* Summary row */}
                <tr style={{ borderTop: '2px solid #e5e7eb', background: '#f9fafb' }}>
                  <td style={{
                    padding: '10px 12px', fontWeight: '600', fontSize: '13px',
                    color: '#374151', fontFamily: FONT,
                    position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1
                  }}>
                    Total Users
                  </td>
                  {applications.map(app => (
                    <td key={app.id} style={{
                      padding: '8px', textAlign: 'center', fontWeight: '600', fontSize: '13px',
                      color: '#374151', fontFamily: FONT
                    }}>
                      {getAppUserCount(app.id)}
                    </td>
                  ))}
                  <td style={{
                    padding: '8px', textAlign: 'center', fontWeight: '700', fontSize: '13px',
                    color: '#111827', fontFamily: FONT
                  }}>
                    {userAccess.length}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
